import suites1 from "@/assets/suites-1.jpg";
import suites2 from "@/assets/suites-2.jpg";
import suites3 from "@/assets/suites-3.jpg";
import suites4 from "@/assets/suites-4.jpg";
import hottub from "@/assets/hero-hottub.jpg";
import couple from "@/assets/hero-couple.jpg";
import hotTubExteriorAsset from "@/assets/hot-tub-exterior.jpg.asset.json";
import coupleHotTub from "@/assets/couple-hot-tub.jpg.asset.json";
import nightHotTub from "@/assets/night-hot-tub.png.asset.json";
import natureSuiteAmenities from "@/assets/nature-suite/amenities.jpg.asset.json";
import natureSuiteBedMorning from "@/assets/nature-suite/bed-morning.jpg.asset.json";
import natureSuiteDog from "@/assets/nature-suite/dog.jpg.asset.json";
import natureSuiteExteriorAngle from "@/assets/nature-suite/exterior-angle.jpg.asset.json";
import natureSuiteExteriorFront from "@/assets/nature-suite/exterior-front.jpg.asset.json";
import natureSuiteExteriorWide from "@/assets/nature-suite/exterior-wide.jpg.asset.json";
import natureSuiteFamilyView from "@/assets/nature-suite/family-view.jpg.asset.json";
import natureSuitePatioCouple from "@/assets/nature-suite/patio-couple.jpg.asset.json";
import natureSuitePropertyRow from "@/assets/nature-suite/property-row.jpg.asset.json";
import natureSuiteRobes from "@/assets/nature-suite/robes.jpg.asset.json";
import {
  Bath,
  BedDouble,
  Castle,
  Eye,
  Maximize2,
  Mountain,
  Sparkles,
  Star,
  Sun,
  Users,
  type LucideIcon,
} from "lucide-react";

export type SuiteChip = { icon: LucideIcon; label: string };

export type Suite = {
  id: string;
  name: string;
  tagline: string;
  shortDescription: string;
  longDescription: string;
  occupancy: string;
  bed: string;
  hero: string;
  chips: SuiteChip[];
  photos: string[];
};

const repeatTo = (arr: string[], n: number): string[] => {
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(arr[i % arr.length]);
  return out;
};

export const SUITES_HERO = hotTubExteriorAsset.url;

export const SUITES: Suite[] = [
  {
    id: "nature",
    name: "Nature Suite",
    tagline: "Where the wild meets refined comfort.",
    shortDescription:
      "Wake to wide-open meadow and mountain views through panoramic windows, then settle into the quiet comfort of a suite designed to bring nature close without giving up ease. Climate-controlled, sound-insulated, and thoughtfully appointed, the Nature Suite blends privacy, tranquility, and refined comfort for an effortless escape into the wild.",
    longDescription: "",
    occupancy: "4",
    bed: "King Bed, Queen Sofa Bed",
    hero: suites2,
    chips: [
      { icon: Eye, label: "Unobstructed Views" },
      { icon: Maximize2, label: "Panoramic Windows" },
    ],
    photos: [
      natureSuiteExteriorFront.url,
      natureSuiteExteriorAngle.url,
      natureSuiteExteriorWide.url,
      natureSuiteFamilyView.url,
      natureSuitePatioCouple.url,
      natureSuiteBedMorning.url,
      natureSuiteDog.url,
      natureSuitePropertyRow.url,
      natureSuiteRobes.url,
      natureSuiteAmenities.url,
    ],
  },
  {
    id: "nature-hottub",
    name: "Nature Suite with Hot Tub",
    tagline: "A private soak in open country.",
    shortDescription:
      "Enjoy panoramic meadow and mountain views, climate-controlled comfort, and refined Nature Suite amenities, with a private hot tub set just off the back patio. Designed for two guests, this suite keeps the layout simple and romantic with a king bed, quiet privacy, and an easy place to unwind after days exploring Backland.",
    longDescription: "",
    occupancy: "2",
    bed: "King Bed",
    hero: hotTubExteriorAsset.url,
    chips: [
      { icon: Bath, label: "Hot tub" },
      { icon: Eye, label: "Unobstructed Views" },
      { icon: Maximize2, label: "Panoramic Windows" },
    ],
    photos: [
      hotTubExteriorAsset.url,
      nightHotTub.url,
      coupleHotTub.url,
      natureSuiteExteriorFront.url,
      natureSuiteExteriorAngle.url,
      natureSuiteExteriorWide.url,
      natureSuiteBedMorning.url,
      natureSuiteRobes.url,
      natureSuiteAmenities.url,
    ],
  },
  {
    id: "starview-hottub",
    name: "Nature Suite with Starview + Hot Tub",
    tagline: "Soak under the northern sky.",
    shortDescription:
      "Fall asleep beneath a breathtaking 16-foot Starview Skylight and unwind in your private outdoor hot tub surrounded by quiet wilderness. With panoramic meadow and mountain views, refined interiors, and year-round comfort, this suite is designed for deep relaxation, romantic connection, and unforgettable nights under the stars.",
    longDescription: "",
    occupancy: "2",
    bed: "King Bed",
    hero: hottub,
    chips: [
      { icon: Sun, label: "16' wide skylight" },
      { icon: Bath, label: "Hot tub" },
      { icon: Eye, label: "Unobstructed Views" },
      { icon: Maximize2, label: "Panoramic Windows" },
    ],
    photos: repeatTo([hottub, suites3, suites2, suites4, suites1, couple], 8),
  },
  {
    id: "starview-bunk",
    name: "Nature Suite with Starview + Bunk",
    tagline: "Stargaze together, sleep four.",
    shortDescription:
      "This versatile Nature Suite brings together the best of Backland: panoramic windows, a breathtaking skylight, refined comfort, and a whimsical bunk fort tucked into the layout. It is a space designed for wonder—equally suited to quiet connection, family memories, and nights spent gazing into a sky full of stars.",
    longDescription: "",
    occupancy: "6",
    bed: "King Bed, Bunk Bed Fort, Queen Sofa Bed",
    hero: suites4,
    chips: [
      { icon: Sun, label: "16' wide skylight" },
      { icon: Castle, label: "Bunk Fort" },
      { icon: Eye, label: "Unobstructed Views" },
      { icon: Maximize2, label: "Panoramic Windows" },
    ],
    photos: repeatTo([suites4, suites1, suites3, suites2, couple, hottub], 8),
  },
];

export const SHARED_AMENITIES: string[] = [
  "Panoramic windows",
  "King-sized beds",
  "Full en-suite bathroom with shower",
  "Luxury linens",
  "Electricity",
  "Automatic climate control",
  "Fully insulated & energy-efficient",
  "Parking at your suite",
  "EV charging at reception",
  "Daily housekeeping",
  "Outdoor patio",
  "Complimentary Wi-Fi",
];
